'use client';

import { useState } from 'react';

interface InteractiveJSONProps {
  data: unknown;
  level?: number;
}

const monospaceFontStyle = {
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", Courier, monospace',
};

function StringRenderer({ data }: { data: string }) {
  const [isStringExpanded, setIsStringExpanded] = useState(false);
  const isLongString = data.length > 80;

  if (!isLongString) {
    return (
      <span
        className="inline-block max-w-[500px] break-words text-[12px] text-base-primary-on-light"
        style={monospaceFontStyle}
      >
        &quot;{data}&quot;
      </span>
    );
  }

  return (
    <span className="inline-block max-w-[500px]">
      <span
        className="break-words text-[12px] text-base-primary-on-light"
        style={monospaceFontStyle}
      >
        &quot;{isStringExpanded ? data : data.substring(0, 80)}
      </span>
      {!isStringExpanded && (
        <button
          onClick={() => setIsStringExpanded(true)}
          className="hover:bg-base-tertiary-on-light cursor-pointer text-[12px] text-base-secondary-on-light leading-[18px] h-[18px]"
          style={monospaceFontStyle}
        >
          <span className="text-link-enabled-on-light underline">...</span>&quot;
        </button>
      )}
      {isStringExpanded && (
        <span
          className="text-[12px] text-base-primary-on-light"
          style={monospaceFontStyle}
        >
          &quot;
        </span>
      )}
    </span>
  );
}

function PrimitiveRenderer({ data }: Readonly<{ data: null | undefined | number | boolean }>) {
  if (data === null) {
    return (
      <span
        className="text-[12px] text-base-secondary-on-light"
        style={monospaceFontStyle}
      >
        null
      </span>
    );
  }

  if (data === undefined) {
    return (
      <span
        className="text-[12px] text-base-secondary-on-light"
        style={monospaceFontStyle}
      >
        undefined
      </span>
    );
  }

  return (
    <span
      className="text-[12px] text-base-primary-on-light"
      style={monospaceFontStyle}
    >
      {typeof data === 'boolean' ? data.toString() : data}
    </span>
  );
}

function getArrayItemKey(item: unknown, index: number): string {
  if (typeof item === 'object' && item !== null && 'id' in item) {
    return String(item.id);
  }
  if (typeof item === 'string') {
    return `item-${item.substring(0, 20)}-${index}`;
  }
  return `item-${index}-${JSON.stringify(item).substring(0, 20)}`;
}

function ArrayRenderer({ data, level, isExpanded, toggleExpanded }: Readonly<{
  data: unknown[];
  level: number;
  isExpanded: boolean;
  toggleExpanded: () => void;
}>) {
  if (data.length === 0) {
    return (
      <span
        className="text-[12px] text-base-primary-on-light"
        style={monospaceFontStyle}
      >
        []
      </span>
    );
  }

  return (
    <span>
      <button
        onClick={toggleExpanded}
        className="hover:bg-base-tertiary-on-light cursor-pointer text-left"
      >
        <span
          className="text-[12px] text-base-secondary-on-light hover:text-link-enabled-on-light hover:underline focus:text-link-enabled-on-light focus:underline"
          style={monospaceFontStyle}
        >
          {isExpanded ? '▼' : '▶'} ({data.length}) {isExpanded ? '[' : '[...]'}
        </span>
      </button>
      {isExpanded && (
        <div>
          <div className="relative ml-4 mt-1 block">
            <div className="ml-2">
              {data.map((item, index) => (
                <div
                  key={getArrayItemKey(item, index)}
                  className="mb-1"
                >
                  <div className="flex gap-2">
                    <span
                      className="text-[12px] text-base-secondary-on-light"
                      style={monospaceFontStyle}
                    >
                      {index}:
                    </span>
                    <span
                      className="text-[12px] text-base-secondary-on-light"
                      style={monospaceFontStyle}
                    >
                      <InteractiveJSON
                        data={item}
                        level={level + 1}
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <span
            className="text-[12px] text-base-secondary-on-light"
            style={monospaceFontStyle}
          >
            ]
          </span>
        </div>
      )}
    </span>
  );
}

function ObjectRenderer({ entries, level, isExpanded, toggleExpanded }: Readonly<{
  entries: [string, unknown][];
  level: number;
  isExpanded: boolean;
  toggleExpanded: () => void;
}>) {
  if (entries.length === 0) {
    return (
      <span
        className="text-[12px] text-base-primary-on-light"
        style={monospaceFontStyle}
      >
        {'{'}
      </span>
    );
  }

  return (
    <span>
      <button
        onClick={toggleExpanded}
        className="hover:bg-base-tertiary-on-light cursor-pointer text-left"
      >
        <span
          className="text-[12px] text-base-secondary-on-light hover:text-link-enabled-on-light hover:underline focus:text-link-enabled-on-light focus:underline"
          style={monospaceFontStyle}
        >
          {isExpanded ? '▼' : '▶'} ({entries.length}) {isExpanded ? '{' : '{...}'}
        </span>
      </button>
      {isExpanded && (
        <div>
          <div className="relative ml-4 mt-1 block">
            <div className="ml-2">
              {entries.map(([key, value]) => {
                const isExpandableObject = typeof value === 'object' && value !== null;
                const hasContent = isExpandableObject && (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0);

                return (
                  <div
                    key={key}
                    className="mb-1"
                  >
                    {hasContent ? (
                      <div>
                        <span
                          className="inline text-[12px] text-base-primary-on-light"
                          style={monospaceFontStyle}
                        >
                          &quot;{key}&quot;:
                        </span>
                        <span className="inline">
                          <InteractiveJSON
                            data={value}
                            level={level + 1}
                          />
                        </span>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <span
                          className="text-[12px] text-base-primary-on-light"
                          style={monospaceFontStyle}
                        >
                          &quot;{key}&quot;:
                        </span>
                        <span
                          className="text-[12px] text-base-primary-on-light"
                          style={monospaceFontStyle}
                        >
                          <InteractiveJSON
                            data={value}
                            level={level + 1}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <span
            className="text-[12px] text-base-secondary-on-light"
            style={monospaceFontStyle}
          >
            {'}'}
          </span>
        </div>
      )}
    </span>
  );
}

type RenderDispatch = {
  type: 'primitive';
  data: null | undefined | number | boolean;
} | {
  type: 'string';
  data: string;
} | {
  type: 'array';
  data: unknown[];
} | {
  type: 'object';
  data: Record<string, unknown>;
} | {
  type: 'unknown';
};

function classifyData(data: unknown): RenderDispatch {
  if (data === null || data === undefined) {
    return { type: 'primitive', data };
  }
  if (typeof data === 'string') {
    return { type: 'string', data };
  }
  if (typeof data === 'number' || typeof data === 'boolean') {
    return { type: 'primitive', data };
  }
  if (Array.isArray(data)) {
    return { type: 'array', data };
  }
  if (typeof data === 'object') {
    return { type: 'object', data: data as Record<string, unknown> };
  }
  return { type: 'unknown' };
}

export function InteractiveJSON({
  data,
  level = 0,
}: Readonly<InteractiveJSONProps>) {
  const [isExpanded, setIsExpanded] = useState(level < 5);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const classified = classifyData(data);

  switch (classified.type) {
    case 'primitive':
      return <PrimitiveRenderer data={classified.data} />;
    case 'string':
      return <StringRenderer data={classified.data} />;
    case 'array':
      return (
        <ArrayRenderer
          data={classified.data}
          level={level}
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
        />
      );
    case 'object':
      return (
        <ObjectRenderer
          entries={Object.entries(classified.data)}
          level={level}
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
        />
      );
    case 'unknown':
      return (
        <span
          className="text-[12px] text-base-secondary-on-light"
          style={monospaceFontStyle}
        >
          Unknown type: {typeof data}
        </span>
      );
  }
}
